"use client";

import * as React from "react";

type AnyProps = Record<string, unknown>;

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (value) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(value);
      else if (ref && typeof ref === "object")
        (ref as React.MutableRefObject<T | null>).current = value;
    }
  };
}

function mergeProps(parent: AnyProps, child: AnyProps): AnyProps {
  const out: AnyProps = { ...parent, ...child };
  for (const key of Object.keys(parent)) {
    const isHandler = /^on[A-Z]/.test(key);
    const childValue = child[key];
    const parentValue = parent[key];
    if (isHandler && typeof childValue === "function" && typeof parentValue === "function") {
      out[key] = (...args: unknown[]) => {
        (childValue as (...a: unknown[]) => void)(...args);
        (parentValue as (...a: unknown[]) => void)(...args);
      };
    } else if (key === "className") {
      out[key] = [parentValue, childValue].filter(Boolean).join(" ");
    } else if (key === "style") {
      out[key] = {
        ...((parentValue as object) ?? {}),
        ...((childValue as object) ?? {}),
      };
    }
  }
  return out;
}

export const Slot = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }>(
  ({ children, ...props }, ref) => {
    if (!React.isValidElement(children)) return null;
    const child = children as React.ReactElement<AnyProps & { ref?: React.Ref<HTMLElement> }>;
    return React.cloneElement(child, {
      ...mergeProps(props, child.props),
      ref: mergeRefs(ref, child.props.ref),
    });
  },
);
Slot.displayName = "Slot";
